import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { AuthProvider, useAuth } from '@/lib/auth';
import { I18nProvider, useI18n } from '@/lib/i18n';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import { SettingsProvider } from '@/lib/settings';
import { WorkspaceProvider } from '@/lib/workspace';
import { ConfirmProvider } from '@/components/ConfirmDialog';
import Layout from '@/components/Layout';
import SetupPage from '@/pages/SetupPage';
import ListView from '@/pages/ListView';
import '@/App.css';

const CalendarView     = lazy(() => import('@/pages/CalendarView'));
const KanbanView       = lazy(() => import('@/pages/KanbanView'));
const AnalyticsView    = lazy(() => import('@/pages/AnalyticsView'));
const SettingsPage     = lazy(() => import('@/pages/SettingsPage'));
const CircleView       = lazy(() => import('@/pages/CircleView'));

// Class boundaries can't use hooks, so translate here and pass strings down.
function TranslatedErrorBoundary({ children }) {
  const { t } = useI18n();
  return (
    <ErrorBoundary strings={{
      title: t('error.title'), description: t('error.description'),
      retry: t('error.retry'), reload: t('error.reload'),
    }}>
      {children}
    </ErrorBoundary>
  );
}

function LoadingScreen() {
  const { t } = useI18n();
  return (
    <div className="h-screen flex items-center justify-center bg-background" data-testid="loading-screen">
      <div className="animate-pulse text-muted-foreground font-body">{t('common.loading')}</div>
    </div>
  );
}

function AppRoutes() {
  const { status } = useAuth();

  if (status === 'loading') {
    return <LoadingScreen />;
  }

  if (status === 'setup' || status === 'needToken') {
    return <SetupPage />;
  }

  return (
    <SettingsProvider>
      <WorkspaceProvider>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Navigate to="/list" replace />} />
            <Route path="/list" element={<ListView />} />
            <Route path="/calendar" element={<CalendarView />} />
            <Route path="/circle" element={<CircleView />} />
            <Route path="/kanban" element={<KanbanView />} />
            <Route path="/analytics" element={<AnalyticsView />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/list" replace />} />
        </Routes>
      </Suspense>
      </WorkspaceProvider>
    </SettingsProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <I18nProvider>
        <TranslatedErrorBoundary>
          <ConfirmProvider>
            <BrowserRouter>
              <AuthProvider>
                <AppRoutes />
                <Toaster position="bottom-right" />
              </AuthProvider>
            </BrowserRouter>
          </ConfirmProvider>
        </TranslatedErrorBoundary>
      </I18nProvider>
    </ThemeProvider>
  );
}
